import { useEffect, useMemo, useState } from 'react';

import { SECONDES_AVANT_LE_COMPTEUR, liaEnAttente, type AttenteLia, type SuiviLiaVue } from './attentesLia.js';

/** Ce qui observe les suivis de Lia d'une conversation : le port d'un fil, ou la source de Canaux. */
export interface ObservateurDeSuivisLia {
  observerSuivisLia?: ((conversationId: string, onChange: (suivis: SuiviLiaVue[]) => void, onError?: (error: unknown) => void) => () => void) | undefined;
}

/**
 * LES SUIVIS DE LIA EN COURS dans une conversation, en temps reel. Sans
 * observateur (port ancien, mode demo) ou sur une erreur d'ecoute : aucun
 * suivi, et la regle d'age (`liaEnAttente`) montre quand meme l'attente et
 * son delai. `conversationId` vide : rien n'est ecoute.
 */
export function useSuivisLia(observateur: ObservateurDeSuivisLia | null | undefined, conversationId: string | null | undefined): SuiviLiaVue[] {
  const [suivis, setSuivis] = useState<SuiviLiaVue[]>([]);
  useEffect(() => {
    setSuivis([]);
    if (observateur?.observerSuivisLia === undefined || !conversationId) return;
    return observateur.observerSuivisLia(conversationId, setSuivis, () => setSuivis([]));
  }, [observateur, conversationId]);
  return suivis;
}

/**
 * LES ATTENTES DE LIA de la liste affichee (`liaEnAttente`) et l'horloge qui
 * les fait vivre. L'horloge ne bat QUE tant qu'une bulle de Lia charge : le
 * compteur, puis le passage au delai depasse. `signature` change quand la
 * rangee de Lia change de hauteur ou de contenu : l'hote la met dans les
 * dependances de son defilement vers le bas.
 */
export function useLiaEnAttente(
  messages: Parameters<typeof liaEnAttente>[0] | null,
  suivis: readonly SuiviLiaVue[],
  dansFil: boolean,
): { attentes: AttenteLia[]; maintenant: number; signature: string } {
  const [maintenant, setMaintenant] = useState(() => Date.now());
  const attentes = useMemo(
    () => (messages === null ? [] : liaEnAttente(messages, suivis, maintenant, dansFil)),
    [messages, suivis, maintenant, dansFil],
  );
  const charge = attentes.some((a) => a.echec === null);
  useEffect(() => {
    if (!charge) return;
    setMaintenant(Date.now());
    const minuterie = window.setInterval(() => setMaintenant(Date.now()), 1000);
    return () => window.clearInterval(minuterie);
  }, [charge]);
  const signature = attentes
    .map((a) => [a.messageId, a.echec, a.etape?.label, a.etape?.state, maintenant - a.depuis.getTime() >= SECONDES_AVANT_LE_COMPTEUR * 1000].join(':'))
    .join('|');
  return { attentes, maintenant, signature };
}
